import {useMutation} from '@apollo/client';
import {OptionsObject, SnackbarKey, SnackbarMessage, useSnackbar} from 'notistack';
import {useState} from 'react';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import {FormikState} from 'formik';
import {Intersection} from 'utility-types';
import {CREATE_TASK, UPDATE_TASK} from './tasksQueries';
import useTasks from './useTasks';
import {taskValues} from './CreateTask/tasksFields';
import {CreateTask, CreateTaskVariables} from '../../schema-types/CreateTask';
import {UpdateTask, UpdateTaskVariables} from '../../schema-types/UpdateTask';
import {cleanForCuro} from '../../utils/cleanString';
import OWNERSHIP from '../../constants/Ownership';
import {useAccountContext} from '../../contexts/AccountContext';
import getMinDate from '../../utils/getMinDate';
import {
  OneviewGroups_oneviewGroups,
  OneviewGroups_oneviewGroups_activitySubGroups,
} from '../../schema-types/OneviewGroups';

import {
  TaskActivityStatusEnum,
  TaskPriorityEnum,
  TaskRegardingEntityEnum,
  TaskStatusReasonEnum,
  TaskUrgencyEnum,
} from '../../schema-types/globalTypes';
import {enumToLabel} from '../../utils/typescriptUtils';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {newUTCDate} from '../../utils/newUTCDate';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import validateUUID from '../../utils/validateUUID';
import {UUID} from '../../graphql/uuid';

export const getTaskPriority = (urg: TaskUrgencyEnum): TaskPriorityEnum => {
  const isPriority =
    urg === TaskUrgencyEnum.TAX_YEAR_SENSITIVE || urg === TaskUrgencyEnum.URGENT;
  return isPriority ? TaskPriorityEnum.HIGH : TaskPriorityEnum.NORMAL;
};

const MAX_STRING_LENGTH = 20;
const getMaxLengthString = (txt: string) =>
  txt.length > MAX_STRING_LENGTH ? `${txt.substring(0, MAX_STRING_LENGTH - 1)}...` : txt;

export const getTaskDescription = (
  values: taskValues,
  account?: AccountBasicFragment
) => {
  let thisNotes = values.notes;
  const notesList = values.notes?.split('- Notes: ');

  if (notesList?.length > 1) {
    // eslint-disable-next-line prefer-destructuring
    thisNotes = notesList[1];
  }

  if (!thisNotes || thisNotes === '') {
    thisNotes = 'N/A';
  }

  // this fixes a weird bug with
  // "selectedEntityType": "Handover notes completed and submitted.\n\nDialogue to be run to capture the correct project stage."
  const selectedEntityType = getMaxLengthString(values.selectedEntityType);
  let description = `${enumToLabel(selectedEntityType)}: ${
    values?.selectedEntityTypeDetails
  } - Notes: ${thisNotes}`;
  // Tasks creation required Contact to exist, but trust account may not have contact
  if (
    values.selectedEntityType === TaskRegardingEntityEnum.CONTACT &&
    !values.selectedEntityTypeDetails &&
    (values.contact !== OWNERSHIP.JOINT_CODE || account?.isTrust)
  ) {
    description = `No Contact - Notes: ${thisNotes}`;
  } else if (
    values.contact === OWNERSHIP.JOINT_CODE &&
    values.selectedEntityType === TaskRegardingEntityEnum.CONTACT
  ) {
    description = `Contact: Joint - Notes: ${thisNotes}`;
  }
  return cleanForCuro(description);
};

export const getGroupIdFromSubGroup = (
  subActivityGroupId: string,
  activityGroups: OneviewGroups_oneviewGroups[]
): string | undefined =>
  activityGroups.find((g) =>
    g.activitySubGroups?.find((s) => s.id === subActivityGroupId)
  )?.id;

// activity template name eg holistic cashflow (step 1) for contact/account (step 2)
export const getTaskSubject = (
  subActivityGroupId: string,
  subActivityGroups: OneviewGroups_oneviewGroups_activitySubGroups[]
) => {
  const subactivity = subActivityGroups.find((s) => s.id === subActivityGroupId);

  return subactivity?.name;
};

export const fixSelectedEntityType = (
  selectedEntityType: TaskRegardingEntityEnum
): TaskRegardingEntityEnum => {
  const selectedEntityTypeAsString = String(selectedEntityType);
  if (selectedEntityTypeAsString === 'GOAL') {
    return TaskRegardingEntityEnum.PLANNING_OBJECTIVE;
  }
  return selectedEntityType;
};

export const useSaveTaskFactory = ({
  enqueueSnackbar,
  account,
  activityGroups,
  subActivityGroups,
  navigate,
  accountId,
  resetForm,
}: {
  navigate: NavigateFunction;
  accountId?: string;
  subActivityGroups: OneviewGroups_oneviewGroups_activitySubGroups[];
  activityGroups: OneviewGroups_oneviewGroups[];
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey;
  account: AccountBasicFragment | undefined;
  resetForm: (nextState?: Partial<FormikState<any>> | undefined) => void;
}) => {
  const [createTask] = useMutation<CreateTask, CreateTaskVariables>(CREATE_TASK, {
    refetchQueries: ['ListTasks'],
    awaitRefetchQueries: true,
  });
  const [updateTask] = useMutation<UpdateTask, UpdateTaskVariables>(UPDATE_TASK, {
    refetchQueries: ['ListTasks'],
    awaitRefetchQueries: true,
  });

  return async ({
    values,
    shouldLeaveAfterSave,
    reviewId,
  }: {
    values: taskValues;
    shouldLeaveAfterSave: boolean;
    reviewId: string;
  }) => {
    if (!reviewId) {
      enqueueSnackbar('Please choose a review', {variant: 'error'});
      return {};
    }
    if (!values.subgroupId) {
      enqueueSnackbar('Please select a task', {variant: 'error'});
      return {};
    }
    try {
      type sharedVariables = Intersection<
        Omit<CreateTaskVariables, 'reviewId'>,
        Omit<UpdateTaskVariables, 'taskId'>
      >;
      let contactId: UUID | undefined;
      if (!account?.isTrust) {
        contactId =
          values.contact === OWNERSHIP.JOINT_CODE
            ? account?.primaryContact?.id
            : values.contact || account?.primaryContact?.id;
      }

      const variables: sharedVariables = {
        activityGroupId: getGroupIdFromSubGroup(values.subgroupId, activityGroups),
        activitySubGroupId: values.subgroupId,
        regardingId: reviewId,
        contactId,
        regardingEntity: TaskRegardingEntityEnum.REVIEW,
        urgency: values.urgency,
        dueDate: getMinDate({
          today: newUTCDate(),
          dateValue: values.deadline,
          minutesOffset: 10,
        }).toISOString(),
        description: getTaskDescription(values, account),
        isActivityReport: false,
        isWaiting: false,
        activityStatus: TaskActivityStatusEnum.OPEN,
        statusReason: TaskStatusReasonEnum.NOT_STARTED,
        subject: getTaskSubject(values.subgroupId, subActivityGroups),
        priority: getTaskPriority(values.urgency),
        percentComplete: 0,
        selectedEntityType: fixSelectedEntityType(values.selectedEntityType),
        selectedEntityId: values.regarding,
      };
      if (values.taskId) {
        const {data: updateData} = await updateTask({
          variables: {
            ...variables,
            taskId: validateUUID(values.taskId),
          },
        });
        if (!updateData?.updateTask?.ok) {
          return enqueueSnackbar(
            `Unable to save task. ${updateData?.updateTask?.errors?.join('...')}`,
            {variant: 'error'}
          );
        }
      } else {
        const {data: createData} = await createTask({
          variables: {
            ...variables,
            regardingId: reviewId,
          },
        });
        if (!createData?.createTask?.ok) {
          return enqueueSnackbar(
            `Unable to save task. ${createData?.createTask?.errors?.join('...')}`,
            {variant: 'error'}
          );
        }
      }

      enqueueSnackbar('Saved task.', {variant: 'success'});
      if (shouldLeaveAfterSave) {
        navigate({
          pathname: `/account/${accountId}/review-journey/${reviewId}/tasks`,
        });
      } else {
        navigate({
          pathname: `/account/${accountId}/review-journey/${reviewId}/tasks-builder`,
        });
        resetForm();
      }
    } catch (e) {
      enqueueSnackbar(`Failed to save task. ${e}`, {variant: 'error'});
    }
  };
};

export default function useSaveTask() {
  const {account, accountId} = useAccountContext();
  const {resetForm} = useAutoForm();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const {subActivityGroups, activityGroups} = useTasks();
  const [saveAndExiting, setSaveAndExiting] = useState(false);
  const [saveAndContinuing, setSaveAndContinuing] = useState(false);

  const save = useSaveTaskFactory({
    enqueueSnackbar,
    account,
    activityGroups,
    subActivityGroups,
    navigate,
    accountId,
    resetForm,
  });

  const saveAndExit = async (values: taskValues, reviewId: string) => {
    setSaveAndExiting(true);
    try {
      await save({values, shouldLeaveAfterSave: true, reviewId});
    } finally {
      setSaveAndExiting(false);
    }
  };
  const saveAndContinue = async (values: taskValues, reviewId: string) => {
    setSaveAndContinuing(true);
    try {
      await save({values, shouldLeaveAfterSave: false, reviewId});
    } finally {
      setSaveAndContinuing(false);
    }
  };
  return {
    saveAndExit,
    saveAndContinue,
    saveAndContinuing,
    saveAndExiting,
  };
}
